import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * useRepositorySelection — historia 170 (Repositorio Documental), J4a/J4b. RN34b/CU8 A5: the
 * destination window a document repository row navigates to (`state.repositorySelection`) is
 * delivered ONCE and then wiped from the history entry (`replace`), so reloading the page or
 * navigating back/forward to it never reselects the same record again (S96).
 *
 * `generic/hooks/` is exempt from CLAUDE.md's "no null/undefined in business logic" rule (that rule
 * only applies under `src/components/` excluding `generic/`) — `null` here is the idiomatic "no
 * selection" value, matching `location.state`'s own shape.
 *
 * This hook is called from the ~11 destination window components (J4b): every caller — production
 * and test — is expected to render inside a Router (a real one, or `<MemoryRouter>` in tests), same
 * as any other component using `useLocation`/`useNavigate` in this codebase. Pre-existing unit tests
 * for those components that did not need routing before this historia now wrap their render in
 * `<MemoryRouter>` (or complete their own `jest.mock('react-router-dom', ...)` stub with
 * `useLocation`/`useNavigate`) — see each test file's own history for the specific fix.
 */
const useRepositorySelection = () => {
  const location = useLocation()
  const navigate = useNavigate()

  // Captured once, at mount: later `replace` calls change `location.state` for FUTURE mounts of this
  // same route (reload, back/forward), never this already-rendered instance's own return value.
  const [selection] = useState(() => (location.state && location.state.repositorySelection) || null)

  useEffect(() => {
    if (!location.state || !location.state.repositorySelection) return
    const { repositorySelection, ...restState } = location.state
    navigate(location.pathname + location.search, { replace: true, state: restState })
    // Runs once, at mount: this hook only ever consumes the ORIGINAL history entry it landed on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return selection
}

export default useRepositorySelection
